import type { QueryClient } from '@tanstack/react-query';
import type { LoaderFunctionArgs } from 'react-router';

import { z } from 'zod';

import { SEARCH_MIN_CHARS } from '#components/Drilldown/Toolbar.utils.ts';
import { getParsedSearchParams as getBaseParsedSearchParams } from '#routes/Home/Home.utils.ts';
import { requireAuthorizedInsightsUser } from '#utils/auth.ts';

const defaultSearchParams = {
  page: 1,
  pageSize: 25,
} as const;

const sortByTupleSchema = z.tuple([z.string(), z.enum(['ASC', 'DESC'])]);

const searchParamsSchema = z.object({
  page: z
    .number()
    .min(1)
    .default(defaultSearchParams.page)
    .catch(defaultSearchParams.page),
  // Must be in sync with the page size options in the DataGrid
  pageSize: z
    .union([
      z.literal(10),
      z.literal(25),
      z.literal(50),
      z.literal(100),
      z.literal(250),
      z.literal(500),
      z.literal(1_000),
      z.literal(5_000),
      z.literal(10_000),
    ])
    .default(defaultSearchParams.pageSize)
    .catch(defaultSearchParams.pageSize),
  sortBy: z
    .string()
    .optional()
    .catch(undefined)
    .transform((value) =>
      value ? value.split(',').map((item) => item.split(' ')) : []
    )
    .pipe(
      z
        .any()
        .array()
        .transform((items) => {
          const filteredItems = items
            .map((item) => {
              const result = sortByTupleSchema.safeParse(item);
              return result.success
                ? `${result.data[0]} ${result.data[1]}`
                : undefined;
            })
            .filter(Boolean) as string[];
          return filteredItems.length > 0 ? filteredItems : ['id ASC'];
        })
    ),
  searchText: z.string().min(SEARCH_MIN_CHARS).optional().catch(undefined),
});

export const loader =
  (queryClient: QueryClient) => async (args: LoaderFunctionArgs) => {
    await requireAuthorizedInsightsUser(queryClient);

    const searchParams = new URL(args.request.url).searchParams;

    const baseResult = getBaseParsedSearchParams(searchParams);

    const result = searchParamsSchema.parse({
      page: Number(searchParams.get('page')),
      pageSize: Number(searchParams.get('pageSize')),
      sortBy: searchParams.get('sortBy'),
      searchText: searchParams.get('searchText'),
    });

    return {
      ...baseResult,
      ...result,
      sortBy: result.sortBy.join(','),
      dataGridInitialState: {
        pagination: {
          paginationModel: {
            page: result.page - 1,
            pageSize: result.pageSize,
          },
        },
        sorting: {
          sortModel: result.sortBy.map((item) => {
            // Safe to use `as` as long as we keep it in sync with the schema
            const [field, sort] = item.split(' ') as z.output<
              typeof sortByTupleSchema
            >;
            return { field, sort: sort.toLowerCase() as 'asc' | 'desc' };
          }),
        },
        filter: {
          filterModel: {
            items: [],
            quickFilterValues: result.searchText?.split(' ') ?? [],
          },
        },
      },
    };
  };
